import React, {useState} from 'react';
import {
  Flex,
  Row,
  Col,
  Layout, Space, Table,
} from "antd";
import {t} from "i18next";
import Title from "antd/es/typography/Title";
import LayoutSider from "../../components/brief/LayoutSider";
import useSWR from "swr";
import urls from "../../api/urls";
import {api} from "../../api/apiProvider";
import dayjs, {Dayjs} from "dayjs";
import minMax from 'dayjs/plugin/minMax'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import '../../assets/gant.scss';
import useTableData, {DataType} from "./useTableData";
import Legend from "./Legend";
import Filter from "./Filter";
import Roles from "../../roles/Roles";
import Forbidden from "../results/forbidden";

const {Header, Content,} = Layout
dayjs.extend(minMax)
dayjs.extend(isSameOrBefore)

export type UserValue = {
  label?: string,
  value?: string,
}

export interface BriefFilter {
  company?: UserValue,
  pool?: UserValue,
  products?: UserValue[],
  period?: [Dayjs | null, Dayjs | null] | null,
  status?: UserValue,
}

const dateFormat = 'YYYY-MM-DD'

export default function Diagram() {
  const [briefFilter, setBriefFilter] = useState<BriefFilter>({})

  const getBriefsParamsString = () => {
    const params = new URLSearchParams([
      ['t[]', 'brief:stage'], ['t[]', 'brief:name'], ['t[]', 'brief:id'], ['t[]', 'brief:product'],
      ['t[]', 'dict:product'], ['t[]', 'product:company'],
      ['t[]', 'company'], ['t[]', 'company:pool'], ['t[]', 'pool'], ['t[]', 'dict:kpi'], ['t[]', 'brief:kpi'],
      ['t[]', 'brief:version'], ['t[]', 'company:pool'], ['t[]', 'pool'], ['t[]', 'dict'], ['t[]', 'brief:kpi'],
    ])

    params.append('order[products.company.pool.id]', 'asc')
    params.append('order[products.company.id]', 'asc')
    params.append('order[products.id]', 'asc')
    params.append('order[stages.order]', 'asc')
    params.append('pagination', 'false')
    params.append('isActive', 'true')

    if (briefFilter.pool) {
      params.append('products.company.pool.id', String(briefFilter.pool.value))
    }

    if (briefFilter.company) {
      params.append('products.company.id', String(briefFilter.company.value))
    }

    if (briefFilter.products) {
      for (let product of briefFilter.products) {
        params.append('products.id[]', String(product.value))
      }
    }

    if (briefFilter.status) {
      if (briefFilter.status.value === 'inWork') {
        params.append('isAllowed', 'false')
      } else if (briefFilter.status.value === 'completed') {
        params.append('isAllowed', 'true')
      }
    }

    if (briefFilter.period && briefFilter.period.length > 0) {
      if (briefFilter.period[0] && briefFilter.period[1]) {
        params.append('stages.dateEnd[after]', briefFilter.period[0].format(dateFormat))
        params.append('stages.dateEnd[before]', briefFilter.period[1].format(dateFormat))
      }
    }

    return params.toString()
  }

  const {
    data: briefs,
    mutate: mutateBriefs,
    isValidating,
  } = useSWR<{
    data: DataType[],
    total: Number,
  }>(`${urls.briefs}?${getBriefsParamsString()}`, api.get, {
    revalidateOnFocus: false
  })

  const {columns, data} = useTableData(
    briefs?.data,
    briefFilter?.period ? briefFilter?.period[0] : null,
    briefFilter?.period ? briefFilter?.period[1] : null,
  )

  return (
    <Layout>
      <Header className={'layout-header'}>
        <Row justify={"space-between"} style={{flex: 1}} align={"middle"}>
          <Col span={8}></Col>
          <Col span={8}>
            <Flex justify={'center'}><Title level={5} style={{margin: 0}}>{t('sidebar.gant')}</Title></Flex>
          </Col>
          <Col span={8}></Col>
        </Row>
      </Header>
      <Layout style={{paddingLeft: '48px'}}>
        <LayoutSider/>
        <Layout>
          <Content
            style={{
              margin: 0,
              minHeight: 280,
              background: '#F1F5F7',
              padding: 16,
            }}
          >
            {Roles.hasAccess('canReadDiagram')
              ? <div className={'page-wrapper'}>
                <section className={'gant'}>
                  <Space direction="vertical" size={12} style={{width: '100%'}}>
                    <Flex vertical gap={10}>
                      <Filter briefFilter={briefFilter} setBriefFilter={setBriefFilter}/>

                      <Flex vertical gap={32}>
                        <Table
                          className={'gant-table'}
                          columns={columns}
                          dataSource={data}
                          rowKey={'id'}
                          scroll={{x: 1000}}
                          size={'middle'}
                          loading={isValidating}
                          bordered
                          pagination={false}
                          sticky={{ offsetHeader: 48 }}
                        />
                        <Legend />
                      </Flex>
                    </Flex>
                  </Space>
                </section>
              </div>
              : <Forbidden/>
            }

          </Content>
        </Layout>
      </Layout>
    </Layout>
  )
}
